import cn from 'classnames';
import React, { ReactNode, useEffect, useState } from 'react';

interface PaginationComponentProps {
  currentPage: number;
  handlePagination?: (page: number, oldPage?: number, totalPages?: number) => void;
  totalPages: number;
}

interface IPaginationChip {
  idx: number;
  pageNumber: number;
  type: 'number-chip' | 'blank-chip' | 'empty';
}

// maximum available pagination number chips, excluding the arrow chip [>]
const PAGINATION_MAX_CHIP_COUNT = 7;

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  currentPage,
  handlePagination,
  totalPages,
}) => {
  // local current page state, also changes as props.currentPage side effect
  const [page, setPage] = useState<number>(currentPage);

  // if there are less then 7 pages, use page count as chip count
  const paginationChipCount =
    PAGINATION_MAX_CHIP_COUNT > totalPages ? totalPages : PAGINATION_MAX_CHIP_COUNT;

  // create a placeholder array for pagination chips, starting from 1
  let pageArray: Array<IPaginationChip> = new Array(paginationChipCount)
    .fill('pagination-mock-holder')
    .map((i, index) => {
      return { idx: index, pageNumber: index + 1, type: 'empty' };
    });

  // change page handler
  const handleChange = (page): void => {
    setPage(page);
    if (handlePagination) {
      //send new page, old page and total pages
      handlePagination(page, totalPages, currentPage);
    }
  };

  // use props.currentPage as source of truth
  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  // pagination chip element [number]
  const PaginationNumber = ({ num }): ReactNode => {
    return (
      <button
        onClick={() => {
          handleChange(num);
        }}
        className={cn(
          'text-grey-800 mr-1 flex h-8 w-8 items-center justify-center rounded-md text-sm font-extrabold lg:mr-2',
          page === num ? 'border-primary-main border-2' : 'border-grey-100 border'
        )}
      >
        {num}
      </button>
    );
  };

  // pagination blank element [...]
  const PaginationDots = (): ReactNode => {
    return (
      <span
        className={cn(
          'border-grey-100 text-grey-800 mr-1 flex h-8 w-8 items-center justify-center rounded-md border text-sm font-extrabold lg:mr-2'
        )}
      >
        ...
      </span>
    );
  };

  // if there are less then 8 pages render all without pagination-blank element
  // [1] [2] [3] [4] [5] [6] [7]
  if (totalPages < 8) {
    pageArray = pageArray.map((item, idx) => {
      return { idx, pageNumber: item.pageNumber, type: 'number-chip' };
    });
  } else {
    // check where is current page right now;

    // if the CURRENT PAGE is one of FIRST FOUR  (like 1|2|3|4 /20)
    // render first 5 number chips, a blank chip and the last number chip
    // [1] [2] [3] [4] [5] [...] [20]
    if (page < 5) {
      pageArray = pageArray.map((item, idx) => {
        if (item.pageNumber < 6) {
          return { idx, pageNumber: item.pageNumber, type: 'number-chip' };
        } else if (item.pageNumber === paginationChipCount) {
          return {
            idx,
            pageNumber: totalPages,
            type: 'number-chip',
          };
        } else {
          return { idx, pageNumber: -1, type: 'blank-chip' };
        }
      });
    }
    // if the CURRENT PAGE is one of LAST FOUR (like 17|18|19|20 /20)
    // render first number chip, a blank chip and the last five number chips
    // [1] [...] [16] [17] [18] [19] [20]
    else if (page > totalPages - 4) {
      pageArray = pageArray.map((item, idx) => {
        if (item.pageNumber === 1) {
          return { idx, pageNumber: 1, type: 'number-chip' };
        } else if (item.pageNumber === 2) {
          return { idx, pageNumber: -1, type: 'blank-chip' };
        } else {
          return {
            idx,
            pageNumber: totalPages - (paginationChipCount - item.pageNumber),
            type: 'number-chip',
          };
        }
      });
    }
    // if the CURRENT PAGE is between [4th] and [length - 4th] (like 5|6|7...15|16 /20)
    // render first number chip, a blank chip, 3 number chips with selected in middle, anther blank chip and last number chip
    // [1] [...] [8] [9] [10] [...] [20] (here is 9/20)
    else {
      pageArray = pageArray.map((item, idx) => {
        switch (item.pageNumber) {
          case 1:
            return { idx, pageNumber: 1, type: 'number-chip' };
          case 3:
            return { idx, pageNumber: page - 1, type: 'number-chip' };
          case 4:
            return { idx, pageNumber: page, type: 'number-chip' };
          case 5:
            return { idx, pageNumber: page + 1, type: 'number-chip' };
          case 7:
            return {
              idx,
              pageNumber: totalPages - (paginationChipCount - item.pageNumber),
              type: 'number-chip',
            };

          default:
            return { idx, pageNumber: -1, type: 'blank-chip' };
        }
      });
    }
  }

  return (
    <div className="flex h-12 items-center justify-between">
      {pageArray.map((page) => {
        if (page.type === 'number-chip') {
          return <PaginationNumber key={page.idx} num={page.pageNumber} />;
        } else {
          return <PaginationDots key={page.idx} />;
        }
      })}
      <button
        disabled={currentPage === totalPages}
        onClick={() => {
          handleChange(page + 1);
        }}
        className={cn(
          'border-grey-100 text-text-light-bg-secondary flex h-8 w-8 items-center justify-center rounded-md border text-sm font-extrabold'
        )}
      >
        {'>'}
      </button>
    </div>
  );
};

export default PaginationComponent;
